/**
 * Dashboard services translations: email, shop, packet
 */

export const email = {
  title: 'Email',
  subtitle: 'Manage your email accounts and aliases',
  stats: {
    total: 'Total',
    accounts: 'Accounts',
    aliases: 'Aliases',
    active: 'Active',
    inactive: 'Inactive'
  },
  domainSelector: {
    label: 'Domain',
    all: 'All domains',
    filtering: 'Filtering by {domain}'
  },
  searchPlaceholder: 'Search and press Enter...',
  addEmail: 'Add email',
  table: {
    email: 'Email',
    nameRecipient: 'Name / Recipient',
    type: 'Type',
    usage: 'Storage',
    actions: 'Actions'
  },
  types: {
    account: 'Account',
    alias: 'Alias'
  },
  edit: 'Edit',
  delete: 'Delete',
  viewDetails: 'View details',
  loading: 'Loading email accounts...',
  empty: {
    title: 'No email accounts available',
    description: 'Create a new email account or alias to get started.',
    action: 'Create email'
  },
  noResults: 'No emails found for the current filter',
  copiedToClipboard: 'Successfully copied',
  copiedMessage: '\'{email}\' was successfully copied to the clipboard',
  slider: {
    titleNew: 'New email',
    titleEdit: 'Edit email',
    subtitleCreate: 'create',
    subtitleUpdate: 'update',
    typeLabel: 'Type',
    emailAddressLabel: 'Email address',
    emailAddressRequired: 'Email address *',
    addressReadOnlyHint: "The address can't be changed after creation — delete the entry and create a new one to use a different address.",
    namePlaceholder: 'user',
    domainPlaceholder: 'domain.com',
    senderLabel: 'Sender addresses',
    recipientLabel: 'Recipient',
    passwordLabel: 'Password',
    passwordLabelRequired: 'Password *',
    passwordPlaceholder: 'Enter password',
    passwordConfirmPlaceholder: 'Confirm password',
    activeLabel: 'Active',
    activeHint: 'Email address is active and can receive messages',
    cancel: 'Cancel',
    save: 'Save',
    create: 'Create',
    required: 'Required field',
    nameRules: 'Allowed characters: a-z A-Z 0-9 - .',
    domainRules: 'You can only use domains that you own',
    emailFormat: 'Invalid email format',
    passwordWeak: 'Password not strong enough',
    passwordMismatch: 'Passwords do not match',
    createSuccess: 'Email was created',
    updateSuccess: 'Email was updated',
    saveError: 'Email could not be saved'
  },
  deleteModal: {
    title: 'Delete account',
    confirmMessage: 'Are you sure? This action cannot be undone!',
    confirmLabel: 'Enter {email}',
    confirmPlaceholder: '{email}',
    cancel: 'Cancel',
    delete: 'Delete',
    error: 'Delete failed'
  },
  senderRecipient: {
    senderTooltip: 'Additional sender address(es) for this account. The user can send emails with this address even if it does not exist locally. Enter one address per input. Press ENTER to add a new entry.',
    recipientTooltip: 'Addresses this alias should forward to. Enter only one address per input and press ENTER to add a new entry.',
    emailFormat: 'Invalid email format',
    domainMismatch: 'Domains must match'
  },
  password: {
    required: 'Required field',
    weak: 'Password not strong enough',
    mismatch: 'Passwords do not match'
  },
  tabs: {
    mailboxes: 'Mailboxes',
    domain: 'Domain Settings',
    subdomains: 'Subdomains',
    delegates: 'Delegates',
    dns: 'DNS & Setup'
  },
  quickAdd: {
    title: 'Add email for external domain',
    button: 'Add External Domain',
    placeholder: 'e.g. example.com',
    addToCart: 'Add to cart',
    addedToCart: 'Added to cart',
    productAddedToCart: 'Email Hosting for {domain} added',
    goToCart: 'Go to cart',
    error: {
      invalid: 'Please enter a valid domain',
      taken: 'This domain is already in your cart'
    }
  }
} as const

export const shop = {
  title: 'Shop',
  subtitle: 'Products and services for your business',
  viewProducts: 'View products',
  whyBuyHere: 'Why buy from us?',

  categories: {
    webhosting: {
      title: 'Web Hosting',
      description: 'Reliable hosting with SSD storage, SSL certificates and email mailboxes.',
      features: {
        sftpCron: 'SFTP & cron jobs',
        freeSsl: 'Free SSL',
        emailIncluded: 'Email included'
      }
    },
    kubernetes: {
      title: 'Kubernetes',
      description: 'Scalable container orchestration with our managed Kubernetes platform.',
      features: {
        autoScaling: 'Auto Scaling',
        loadBalancing: 'Load Balancing',
        cicdIntegration: 'CI/CD Integration'
      }
    },
    vserver: {
      title: 'VServer (VPS)',
      description: 'Full root access on dedicated NVMe with included snapshots.',
      features: {
        fullRoot: 'Full root access',
        snapshots: 'Included snapshots',
        ipv6Slash112: 'IPv6 /112 per VM'
      }
    },
    domains: {
      title: 'Domains',
      description: 'Secure your desired domain from over 500 available extensions.',
      features: {
        tlds500: 'Over 500 TLDs',
        dnsManagement: 'DNS Management',
        whoisProtection: 'WHOIS protection optional',
        dynDns: 'DynDNS included'
      }
    },
    email: {
      title: 'Email Hosting',
      description: 'Professional email on your own domain with up to 1,000 mailboxes.',
      features: {
        mailboxes: 'Up to 1,000 mailboxes',
        storage: '10 GB pooled storage per domain',
        autoSecurity: 'DKIM/SPF/DMARC automatic'
      }
    }
  },

  benefits: {
    instantActivation: {
      title: 'Activated Automatically',
      description: 'Domains, email, VServers and Kubernetes go live automatically after payment. Web hosting is activated by hand.'
    },
    securePayment: {
      title: 'Secure Payment',
      description: 'Encrypted payment processing'
    },
    flexibleCancellation: {
      title: 'Flexible Cancellation',
      description: 'Cancel monthly, no hidden costs'
    },
    supportIncluded: {
      title: 'Support Included',
      description: 'Personal English-speaking support'
    }
  },

  kubernetes: {
    title: 'Kubernetes',
    subtitle: 'Managed Kubernetes that scales with you',
    loadingProducts: 'Loading products...',

    tabs: {
      namespaces: 'Namespaces',
      dedicatedCluster: 'Dedicated Cluster'
    },

    billingInterval: 'Billing interval',
    intervals: {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      halfAnnually: 'Half-yearly',
      annually: 'Annually'
    },
    perMonth: '/ month',

    popular: 'Popular',

    namespaceFeatures: {
      isolatedNamespace: 'Isolated Namespace',
      dailyBackups: 'Daily Volume Backups',
      monitoring247: '24/7 Monitoring',
      sslIncluded: 'SSL/TLS included',
      egressIncluded: '{amount} GiB egress included'
    },

    clusterFeatures: {
      dedicatedResources: 'Dedicated Resources',
      fullIsolation: 'Full Isolation',
      managedControlPlane: 'Managed Control Plane',
      prioritySupport: 'Priority Support'
    },

    addToCart: 'Add to cart',
    contactUs: 'Contact us',
    addedToCart: 'Added to cart',
    productAddedToCart: '{product} was added to the cart',

    payg: {
      badge: 'Pay-as-you-go',
      subtitle: 'Only pay for what you use',
      baseFee: 'Base fee',
      billingNote: 'Billed by reserved or actual CPU & RAM (whichever is higher). Storage by capacity, egress by traffic.',
      features: {
        noMinimum: 'No minimum usage',
        spendingCap: 'Budget alerts'
      },
      rates: {
        cpuUnit: '/core-hr',
        memoryUnit: '/GiB-hr',
        storageUnit: '/GiB-mo',
        egressUnit: '/GiB'
      }
    },

    whyManaged: 'Why Managed Kubernetes?',
    features: {
      quickStart: {
        title: 'Quick Start',
        description: 'Your namespace is ready in minutes.'
      },
      autoScaling: {
        title: 'Auto-Scaling',
        description: 'Kubernetes handles load spikes automatically.'
      },
      security: {
        title: 'Security & Backups',
        description: 'TLS certificates for your routes, encrypted traffic between nodes and daily backups of volumes and namespace config. Databases need a backup of their own.'
      },
      managed: {
        title: 'Managed Service',
        description: 'Upgrades, patches and 24/7 monitoring.'
      }
    }
  },

  webhosting: {
    title: 'Web Hosting',
    subtitle: 'Reliable hosting for your projects',
    loadingProducts: 'Loading products...',

    billingInterval: 'Billing interval',
    intervals: {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      halfAnnually: 'Half-yearly',
      annually: 'Annually'
    },

    features: {
      storage: 'Storage',
      freeSsl: 'Free SSL certificates',
      emailIncluded: 'Email mailboxes included',
      backups: 'On-demand backups',
      support: 'Support'
    },

    addToCart: 'Add to cart',
    addedToCart: 'Added to cart',
    productAddedToCart: '{product} was added to the cart',

    includedTitle: 'Included in all packages',
    included: {
      sftpCron: 'SFTP & cron jobs',
      uptime: '99% Uptime SLA',
      phpSupport: 'PHP 8.x Support',
      ddosProtection: 'DDoS Protection',
      unlimitedTraffic: 'Unlimited Traffic',
      monthlyCancellation: 'Cancel monthly'
    }
  },

  email: {
    title: 'Email Hosting',
    subtitle: 'Professional email on your own domain',
    loadingProduct: 'Loading product...',
    domainInput: {
      label: 'Domain',
      placeholder: 'e.g. example.com',
      hint: 'The domain you want to set up email hosting for',
      suggestions: 'Your domains:',
      error: {
        invalid: 'Please enter a valid domain',
        taken: 'This domain is already in your cart'
      }
    },
    addToCart: 'Add to cart',
    addedToCart: 'Added to cart',
    productAddedToCart: 'Email Hosting for {domain} added',
    goToCart: 'Go to cart',
    perMonth: '/ month',
    perYear: '/ year',
    features: {
      mailboxes: 'Up to 1,000 mailboxes & 5,000 aliases',
      storage: '{storage} GB pooled storage per domain',
      autoSecurity: 'DKIM/SPF/DMARC automatic',
      outbound: '{count} emails/month included',
      cancelMonthly: 'Cancel monthly',
      cancelYearly: "Cancel any time, one month's notice; paid time after that refunded pro rata"
    }
  }
} as const

export const packet = {
  title: 'Packages',
  subtitle: 'Manage your hosting packages and subscriptions',
  loading: 'Loading packages...',
  restoreError: 'Cancellation could not be revoked',
  cancelError: 'Cancellation could not be completed',
  stripeUnavailable: 'Payment processing could not be reached, so nothing was changed. Please try again in a few minutes.',
  invoiceOpen: 'Pay the open invoice first, then you can revoke the cancellation.',
  stats: {
    total: 'Total',
    active: 'Active',
    canceled: 'Cancelled'
  },
  empty: {
    title: 'No packages available',
    description: 'You do not have any hosting packages yet. Visit our shop to purchase a package.'
  },
  withdrawPage: {
    title: 'Withdraw from contract',
    subtitle: 'Contracts and plan switches whose withdrawal period is still running',
    intro: 'Here you see every contract and plan switch of your account whose withdrawal period is still running. Once your withdrawal has reached us, you receive an acknowledgement of receipt by email.',
    instructionLink: 'Withdrawal instructions',
    loading: 'Loading contracts...',
    empty: {
      title: 'No contract or plan switch within its withdrawal period',
      description: 'At the moment no withdrawal period is running for any of your contracts or plan switches. When you order a package or switch to a higher plan or from the free to a paid plan, you find it here for as long as its withdrawal period runs.'
    }
  },
  // The withdrawal of one plan switch, the part of a contract § 356a BGB
  // lets the customer pick. The sentences match the withdrawal preview and
  // the receipts' wording for what the withdrawal does.
  switchWithdraw: {
    button: 'Withdraw plan switch',
    switched: 'Switch of {switchDate} to {newPlan}',
    restored: 'Your plan is {oldPlan} again as of now.',
    refund: 'For the time from the switch to the receipt of your withdrawal we charge the service on {newPlan} pro rata, which is {wertersatz}. Of the {amount} charged for the switch we refund the rest, {refund}, within 14 days of receiving your withdrawal, to the payment method you used originally.',
    usageCharged: 'Your namespace is back on the free tier as of now. For the time from the switch to the receipt of your withdrawal we charge the pro rata base fee ({wertersatzBase}) and your usage at the agreed prices ({wertersatzUsage}), {wertersatz} in total.',
    // usageCharged without its parts, for the free_pending receipt.
    usageChargedTotal: 'Your namespace is back on the free tier as of now. For the time from the switch to the receipt of your withdrawal we charge the pro rata base fee and your usage at the agreed prices, {wertersatz}.',
    usageSetOff: 'We set this amount off against what you have already paid since the switch ({paid}); we refund the rest, {refund}, within 14 days of receiving your withdrawal, to the payment method you used originally.',
    previewPaid: 'For the time since the switch you pay {wertersatz} for {newPlan}; we set that off against the refund, and you get {refund} back.',
    previewUsage: 'For the time since the switch you pay the pro rata base fee and your usage so far, currently {usageAmount}; what you have already paid since the switch is credited and the rest refunded.',
    previewRefund: 'As of today you get {refund} back.',
    previewOwed: 'As of today you still pay {owed}.',
    contractStays: 'Your existing contract and the amounts you paid for it stay unchanged.',
    byHand: 'We are now carrying out the withdrawal and will confirm in a further email which plan applies to {identifier}. Whatever we charged you for this switch, we refund within 14 days of receiving your withdrawal, to the payment method you used originally. If a payment for the switch is still being processed and does not go through, nothing was charged to you. For the time from the switch to the receipt of your withdrawal we charge the service on the new plan pro rata and set the amount off against the refund; we confirm both no later than with the refund. Your existing contract and the amounts you paid for it stay unchanged.',
    // The receipt's sentences per variant, as the receipt mail words them.
    receipt: {
      freeRestored: 'Your namespace is back on the free tier as of now.',
      nothingCharged: 'We did not charge you for this switch, and the use since the switch is not charged.',
      nothingChargedRefund: 'We charge nothing for the time since the switch; any open amounts for it are cancelled. What you have already paid since the switch ({refund}) is refunded in full within 14 days of receiving your withdrawal, to the payment method you used originally.',
      nothingChargedPending: 'We charge nothing for the time since the switch. A payment since the switch is still being processed: if it arrives, we refund it in full within 14 days of receiving your withdrawal to the payment method you used originally; if it does not, there is nothing to refund. We confirm the settlement by email.',
      paidPending: 'Your plan stays {oldPlan}; the switch did not take effect. The payment of {amount} for the switch is still being processed: if it arrives, we refund it in full within 14 days of receiving your withdrawal to the payment method you used originally; if it does not, nothing was charged to you. We charge nothing for the time since the switch. We confirm the settlement by email.',
      paidReversed: 'Your plan stays {oldPlan}; the switch did not take effect. The {amount} paid for the switch is refunded in full within 14 days of receiving your withdrawal, to the payment method you used originally. We charge nothing for the time since the switch.',
      // A vServer resized back after it ran ahead on the new tier.
      aheadRestored: 'Your plan stays {oldPlan}; the switch did not take effect, and your server is switched back to {oldPlan}. For the time from the switch to the receipt of your withdrawal, while your server ran on {newPlan}, we charge compensation of {wertersatz} (section 12.7 of the terms).',
      aheadPending: 'The payment of {amount} for the switch is still being processed: if it arrives, we set the compensation off against it and refund the rest of {refund} within 14 days of receiving your withdrawal to the payment method you used originally; if it does not, we invoice the compensation separately. We confirm the settlement by email.',
      aheadPendingNoRefund: 'The payment of {amount} for the switch is still being processed: if it arrives, we set the compensation off against it and refund nothing, as the compensation uses up the payment; if it does not, we invoice the compensation separately. We confirm the settlement by email.',
      aheadReversed: 'We set it off against the {amount} paid for the switch and refund the rest of {refund} within 14 days of receiving your withdrawal to the payment method you used originally.',
      aheadReversedNoRefund: 'We set it off against the {amount} paid for the switch; nothing is left to refund.',
      refunded: 'We refund the {amount} charged for the switch within 14 days of receiving your withdrawal, to the payment method you used originally. What you used in the {newPlan} plan since the switch is not charged.',
      usageExactCover: 'We set this amount off against what you have already paid since the switch ({paid}); nothing is left to refund or to pay.',
      usagePartSetOff: 'We credit what you have already paid since the switch ({paid}) against it; we invoice the remaining {owed} separately now.',
      usageInvoiced: 'You have not paid anything since the switch yet; we invoice this amount separately now.',
      older: 'Another switch already followed this one. We therefore carry out the withdrawal by hand and will let you know which plan applies afterwards. Regardless of that, we refund the {amount} charged for the withdrawn switch within 14 days of receiving your withdrawal, to the payment method you used originally.',
      olderWertersatz: 'Your withdrawal concerns the switch of {switchDate} to {newPlan}, which was followed by a further switch on {nextSwitchDate}. For the time from {switchDate} to {nextSwitchDate} we charge the service on {newPlan} pro rata, which is {wertersatz}. Of the {amount} charged for this switch, after deducting the pro rata amount and the amounts already credited, we refund {refund} within 14 days of receiving your withdrawal, to the payment method you used originally. We review which plan your contract is now on in light of the later switches and confirm it to you by email.',
      byHandIntro: 'We are now carrying out the withdrawal and will confirm in a further email which plan applies to {identifier}. Whatever we charged you for this switch, we refund within 14 days of receiving your withdrawal, to the payment method you used originally.',
      byHandPending: 'If a payment for the switch is still being processed and does not go through, nothing was charged to you.',
      byHandPendingWertersatz: 'If a payment for the switch is still being processed and does not go through, there is nothing to refund, and we invoice the pro rata amount separately.',
      byHandChargeLater: 'For the time from the switch to the receipt of your withdrawal we charge the price difference to {newPlan} pro rata, which is {wertersatz}; we charge nothing else for the switch.',
      byHandWertersatz: 'For the time from the switch to the receipt of your withdrawal we charge the service on the new plan pro rata and set the amount off against the refund; we confirm both no later than with the refund.',
      byHandNothing: 'What you used on the {newPlan} plan since the switch is not charged.',
      byHandFreeIntro: 'We are carrying out the withdrawal now and will confirm in a further email that {identifier} is back on the free tier.',
      byHandFreeOwed: 'For the time from the switch to the receipt of your withdrawal we charge the pro rata base fee and your usage at the agreed prices. We credit what you have already paid since the switch against it and refund any surplus within 14 days of receiving your withdrawal to the payment method you used originally; any remaining amount we invoice separately. We state the amounts in the further email.',
      usagePending: 'A payment since the switch is still being processed. Once it has arrived we credit it against this amount and refund the rest within 14 days of receiving your withdrawal to the payment method you used originally; if it does not arrive, we invoice the amount separately. We confirm the settlement by email.',
      noSwitchUntilSettled: 'Until then, no further plan switch is possible.'
    },
    overQuotaK8s: 'Your namespace currently uses more than {oldPlan} includes. Running pods stay; new pods beyond the {oldPlan} quota are refused until usage is back below it.',
    overQuotaHosting: "Your package currently uses {usage} of storage, {oldPlan} includes {quota}. We charge the excess from the day of the withdrawal at your package's additional-storage price ({overagePrice} per GB and month, {vat}).",
    vat: {
      gross: 'incl. VAT',
      net: 'excl. VAT'
    },
    error: {
      alreadyWithdrawn: 'A withdrawal of this plan switch has already reached us. You received the acknowledgement of receipt by email; a second withdrawal is not needed.',
      notWithdrawable: 'This plan switch can no longer be withdrawn here: either the withdrawal period has expired, or a withdrawal has reached us in the meantime. Reload the page to see the current state. If you think the period is still running, write to service{\'@\'}itsh.dev.'
    }
  },
  domains: {
    title: 'Domain contracts',
    hint: 'Domain contracts are cancelled on the domain itself, under "Manage".',
    manage: 'Manage'
  },
  card: {
    status: {
      active: 'Active',
      canceled: 'Cancelled'
    },
    storage: {
      label: 'Storage',
      used: 'used'
    },
    details: {
      canceledUntil: 'Cancelled until',
      activeUntil: 'Active until',
      renewsOn: 'Renews on',
      cancelPeriod: 'Cancellation period',
      cancelPeriodMonthly: 'End of the billing month',
      cancelPeriodTerm: '1 month',
      cancelPeriodNone: 'None, ends at once',
      paymentMethod: 'Payment method',
      refund: 'Refund',
      refundPending: '{amount} after the contract ends',
      refunded: '{amount} refunded on {date}'
    },
    actions: {
      manageEmail: 'Manage email',
      restore: 'Restore',
      cancel: 'Cancel'
    },
    modal: {
      cancel: {
        title: 'Cancel package',
        message: 'Do you really want to cancel your package {name} on {date}?',
        endingDomains: 'These domains are included in the package and have no contract of their own. Cancelling deletes them at the end of their paid period:',
        endingDomainsKeep: 'To keep one, get it a contract of its own or transfer it to another provider.',
        endingDomainContract: 'Own contract',
        endingDomainTransfer: 'Transfer out',
        mailWarning: 'Email on the domains linked to this package keeps running until {date}. After that, mailboxes, aliases and lists there are switched off unless you book email hosting for that domain. The data is retained.',
        overdueNote: 'If a payment for this package is overdue, or becomes overdue before that date, it is suspended right away instead, and so is email on its domains. Paying the open invoice brings it back until the end of the period it covers.',
        overdueNoteTerm: 'If a payment for this package is overdue, or becomes overdue before that date, it is suspended right away instead, and so is email on its domains. Paying the open invoice brings it back until the contract ends, and the paid time after that is refunded pro rata by the day.',
        refund: 'Your package is paid until {paidUntil}. For the paid time after the end of the contract we refund pro rata by the day an estimated {amount} to your original payment method. The refund is made within 14 days after the end of the contract; if you paid by SEPA direct debit, within 14 days after the eight-week period in which your bank can still return the debit has expired.',
        refundNone: 'Your package is paid until {paidUntil}. Nothing of that is left to refund after the contract ends.',
        refundUnknown: 'Your package is paid until {paidUntil}. We refund the paid time after the end of the contract pro rata by the day to your original payment method. The refund is made within 14 days after the end of the contract; if you paid by SEPA direct debit, within 14 days after the eight-week period in which your bank can still return the debit has expired.',
        refundProrated: 'Your package is paid until {paidUntil}. The time from then until the contract ends on {date} is billed pro rata, and there is no refund.',
        refundOverdueVServer: 'Your vServer was paid until {paidUntil}, and the invoice for the period after that is open. Your cancellation ends the contract at once, and nothing of the paid time is left to refund.',
        refundOverdue: 'Your package was paid until {paidUntil}, and the invoice for the period after that is open. Once you pay it, your package runs until the contract ends on {date}, and we refund the paid time after that pro rata by the day to your original payment method. The refund is made within 14 days after the end of the contract; if you paid by SEPA direct debit, within 14 days after the eight-week period in which your bank can still return the debit has expired.',
        confirm: 'Cancel package',
        abort: 'Keep package'
      },
      restore: {
        title: 'Revoke cancellation',
        message: 'Do you want to revoke your cancellation from {date}?',
        confirm: 'Revoke cancellation',
        abort: 'Abort'
      },
      withdraw: {
        title: 'Withdrawal',
        message: 'You are about to withdraw from your contract for {name}. This ends the package.',
        mailWarning: 'Email on the domains linked to this package is switched off within an hour of the withdrawal unless you book email hosting for that domain. The data is retained.',
        refundInfo: 'Refund amount: {amount}',
        refundAll: 'We refund every payment for this contract.',
        refundTimeline: 'The refund is made within 14 days of receipt of your withdrawal to your original payment method.',
        confirm: 'Confirm withdrawal',
        abort: 'Abort'
      }
    },
    withdraw: {
      button: 'Withdraw from contract',
      daysLeft: '{days} days',
      label: 'Right of withdrawal',
      status: 'Withdrawn',
      statusReceived: 'Withdrawal received',
      receivedLabel: 'Withdrawal received on',
      receivedNote: 'We end the contract and refund what you paid for it within 14 days of receipt of your withdrawal.',
      success: {
        title: 'Withdrawal received',
        message: 'Your package has been ended. Refund: {amount}. You receive the acknowledgement of receipt by email.',
        messageNoAmount: 'Your package has been ended. You receive the acknowledgement of receipt by email.',
        received: 'We end the contract and refund what you paid for it within 14 days. You receive the acknowledgement of receipt by email.'
      },
      error: {
        expired: 'The withdrawal period has expired',
        alreadyWithdrawn: 'A withdrawal from this contract has already reached us',
        notSent: 'Your withdrawal could not be sent. Please send it again.',
        domainExempt: 'This domain cannot be withdrawn from online, please write to us.'
      }
    }
  },
} as const

export const kubernetes = {
  title: 'Kubernetes',
  subtitle: 'Usage and billing for your Kubernetes namespace',
  billingPeriod: 'Billing period',
  currentUsage: 'Current usage',
  projectedBill: 'Projected bill',
  projectedDisclaimer: 'Non-binding estimate incl. 19% VAT based on current usage this billing period. The actual invoice amount may differ.',
  projectedDisclaimerNet: 'Non-binding estimate, net with no VAT charged, based on current usage this billing period. The actual invoice amount may differ.',
  paygEstimateTitle: 'What Pay-as-you-go would cost',
  paygEstimateIntro: 'Estimated from this namespace\'s actual measured usage this period — so you can see what switching to Pay-as-you-go would cost.',
  paygEstimateDisclaimer: 'Non-binding estimate incl. 19% VAT. You are not charged for usage on your current plan; this is only what Pay-as-you-go would cost.',
  paygEstimateDisclaimerNet: 'Non-binding estimate, net with no VAT charged. You are not charged for usage on your current plan; this is only what Pay-as-you-go would cost.',
  paygEstimateCta: 'Switch to Pay-as-you-go',
  baseFee: 'Base fee',
  minimumAsleep: 'Minimum (all workloads asleep)',
  total: 'Total',
  totalGross: 'Total',
  lowConfidence: 'Extrapolated from less than a day of data',
  hours: 'hours',
  days: 'days',
  noUsageData: 'No usage data available yet',
  unavailable: 'Kubernetes billing is not configured for this account.',
  noSubscription: {
    title: 'No Kubernetes plan',
    description: 'You don\'t have a Kubernetes plan yet. Get started with a Kubernetes namespace.',
    action: 'View Kubernetes plans'
  },
  spendingCap: {
    title: 'Budget alerts',
    label: 'Budget (EUR)',
    notSet: 'No budget set. Click edit to set up email alerts when your spending approaches the limit.',
    hint: 'You will receive email alerts at 75%, 90% and 100%. This is not a hard limit – your workloads will continue running. Leave empty to remove.'
  },
  notifications: {
    capSaved: 'Spending cap saved',
    kubeconfigDownloaded: 'Kubeconfig downloaded',
    gitConfigured: 'Git repository configured',
    gitRemoved: 'Git configuration removed',
    domainAdded: 'Domain added',
    domainDeleted: 'Domain deleted',
    domainVerified: 'Domain verified'
  },
  config: {
    title: 'Configuration',
    namespace: 'Namespace',
    status: 'Status',
    provisionedAt: 'Provisioned at',
    errorMessage: 'Error',
    downloadKubeconfig: 'Download Kubeconfig',
    docsLink: 'Documentation',
    statuses: {
      provisioning: 'Provisioning',
      active: 'Active',
      failed: 'Failed',
      deprovisioning: 'Deprovisioning',
      deprovisioned: 'Deprovisioned',
      suspended: 'Suspended'
    }
  },
  free: {
    badge: 'Free',
    title: 'Free namespace',
    description:
      'Perpetually free Kubernetes namespace with the full K8s API.',
    limits: {
      cpu: '200m vCPU',
      memory: '512 MiB RAM',
      pods: '5 pods',
      storage: '1 GiB NFS storage',
      egress: '10 GiB egress per month',
      parentDomain: 'Subdomains under *.itsh-apps.dev (no custom domains)'
    },
    claimCta: 'Claim free namespace',
    claimSuccess: 'Your free namespace is ready',
    claimErrors: {
      phoneRequired: 'Please verify your phone number first.',
      alreadyHasFree: 'You have already claimed a free namespace.',
      phoneAlreadyClaimed:
        'This phone number is already linked to another active account.',
      vatDuplicate:
        'This VAT ID is already linked to another free-tier account.',
      disposableEmail:
        'Disposable email addresses are not allowed for the free tier.',
      subnetRateLimited:
        'Too many free-tier claims from your network recently. Try again later or contact support.',
      datacenterNotAllowed:
        'The free tier is not available from cloud or hosting networks (VPN, server, data center). Please claim it over a residential internet connection. Our paid plans have no such restriction.',
      turnstileFailed: 'Bot-protection check failed, please try again.',
      sanctionedJurisdiction:
        'The Free Tier cannot be provided in your region for legal reasons.',
      outOfRegion:
        'The Free Tier is currently available in the EU/EEA, United Kingdom, United States, Canada, Australia, New Zealand, and Switzerland only. Our paid Kubernetes plans are available worldwide.',
      emailNotVerified:
        'Please confirm your email address first. We sent you a link — check your inbox.',
      notAllowed:
        'Your account cannot claim the free tier right now. Please contact support.'
    },
    refusal: {
      paidPlansCta: 'View Kubernetes plans'
    },
    cardGate: {
      message:
        "To confirm your identity we need a payment method on file. Your card won't be charged — only a zero-euro verification is performed.",
      cta: 'Add payment method',
      setupFailed: 'The payment method could not be set up. Please try again.'
    },
    egressBar: {
      label: 'Egress traffic',
      capLabel: 'of {cap} GiB this month',
      warningAt80: 'You have used 80% of your monthly egress allowance.',
      reachedCap:
        'Monthly egress cap reached. Namespace suspended; auto-resumes on the 1st of next month.'
    },
    inactivityCountdown: {
      warningTitle: 'Inactivity detected',
      warningBody:
        'We have not seen any traffic in the last {days} days. After 30 days of inactivity the namespace will be suspended.',
      suspendedTitle: 'Suspended for inactivity',
      suspendedBody:
        '30 days without traffic. Click "Resume" to reactivate your namespace.'
    },
    suspendBanner: {
      titleEgressCap: 'Free-tier egress cap reached',
      titleInactivity: 'Suspended for inactivity',
      titleAbuse: 'Suspended due to an abuse complaint',
      titleGeneric: 'Namespace suspended',
      resumeButton: 'Resume',
      upgradeButton: 'Upgrade to a paid tier',
      autoReset: 'Auto-resumes on {month} 1.',
      contactSupport: 'Please contact support.'
    },
    customDomain: {
      blocked: 'Custom domains',
      blockedTooltip:
        'Custom domains are not available on the free tier. Upgrade to a paid tier to use your own domains.',
      upgradeCta: 'Upgrade now'
    },
    upgrade: {
      title: 'Upgrade free → paid',
      description:
        'Your workloads keep running with no interruption: same namespace, same kubeconfig.',
      paygTitle: 'Pay-as-you-go',
      paygDescription:
        'Usage-based billing. Full quotas, custom domains, no egress cap.',
      cartFallback:
        'Fixed-price tiers (S/M/L) are available via the shopping cart.',
      success: 'Upgrade complete. Welcome to the paid tier.'
    },
    release: {
      confirmTitle: 'Release free namespace',
      confirmBody:
        'This deletes your namespace and any workloads running in it. Due to free-tier rules (one free claim per account, lifetime), you will not be able to claim a new free namespace afterwards.',
      confirmAction: 'Release permanently',
      success: 'Free namespace released.'
    }
  },
  git: {
    title: 'GitOps (ArgoCD)',
    repoUrl: 'Repository URL',
    path: 'Path',
    revision: 'Revision',
    selfHeal: 'Self-heal',
    syncStatus: 'Sync Status',
    healthStatus: 'Health Status',
    notConfigured: 'No Git repository configured.',
    configure: 'Configure',
    remove: 'Remove',
    confirmRemove: 'Are you sure you want to remove the GitOps configuration?'
  },
  domains: {
    title: 'Custom Domains',
    addDomain: 'Add Domain',
    domain: 'Domain',
    status: 'Status',
    verified: 'Verified',
    pending: 'Pending',
    verificationInstructions: 'Add the following TXT record to your DNS:',
    txtRecord: 'TXT Record',
    host: 'Host',
    value: 'Value',
    verify: 'Verify',
    noDomains: 'No custom domains configured.',
    autoDns: 'Auto-DNS',
    autoDnsHint: 'Automatically create DNS records pointing to the cluster',
    autoDnsEnabled: 'Auto-DNS enabled',
    autoDnsDisabled: 'Auto-DNS disabled'
  },
  overview: {
    title: 'Overview',
    quotaUsage: 'Resource Quotas',
    workloads: 'Workloads',
    used: 'Used',
    limit: 'Limit'
  },
  tabs: {
    overview: 'Overview',
    workloads: 'Workloads',
    pods: 'Pods',
    events: 'Events',
    storage: 'Storage',
    usage: 'Usage',
    config: 'Config'
  },
  workloads: {
    title: 'Workloads',
    name: 'Name',
    kind: 'Kind',
    replicas: 'Replicas',
    age: 'Age',
    noWorkloads: 'No workloads deployed.',
    health: 'Health'
  },
  pods: {
    title: 'Pods',
    name: 'Name',
    status: 'Status',
    restarts: 'Restarts',
    age: 'Age',
    containers: 'Containers',
    noPods: 'No pods running.'
  },
  events: {
    title: 'Events',
    type: 'Type',
    reason: 'Reason',
    message: 'Message',
    object: 'Object',
    time: 'Time',
    count: 'Count',
    noEvents: 'No recent events.',
    all: 'All',
    warning: 'Warning',
    normal: 'Normal'
  },
  storage: {
    title: 'Persistent Volumes',
    name: 'Name',
    capacity: 'Capacity',
    used: 'Used',
    status: 'Status',
    storageClass: 'Storage Class',
    mountedBy: 'Mounted By',
    age: 'Age',
    noStorage: 'No persistent volumes configured.',
    autoscaling: 'Auto-expand',
    autoscalingLimit: 'Limit',
    billedAs: 'Billed as {size}',
    billableHint: 'Block storage minimum size',
    databaseBackups: 'The daily platform backup at 03:00 UTC copies only the volumes a running pod has mounted at that moment, so the volume of an app scaled to zero is missing from it. It does not guarantee a consistent state for databases, so set up a backup of your own for them.',
    platformBackupsLink: 'What is backed up',
    databaseBackupsLink: 'Set up database backups',
    statuses: {
      bound: 'Bound',
      pending: 'Pending',
      lost: 'Lost'
    }
  },
  logs: {
    container: 'Container',
    lines: 'Lines',
    autoRefresh: 'Auto-refresh',
    noLogs: 'No logs available.',
    notStarted: 'This container is still starting, so it has no logs yet.',
    requestRejected: 'Logs could not be fetched for this container.',
    podGone: 'This pod no longer exists.'
  },
  costByWorkload: 'Cost by workload',
  costByWorkloadEstimate: 'Estimated cost by workload (Pay-as-you-go)',
  usageHistory: 'Usage history',
  sleep: {
    title: 'Scale-to-Zero',
    intro: 'These workloads scale down to zero replicas when no requests come in. While they sleep you are not billed for CPU or memory. Storage and the base fee continue.',
    state: 'State',
    asleep: 'Asleep',
    awake: 'Awake',
    whenAwake: 'Cost when awake',
    perHour: '≈ {amount}/h'
  },
  efficiency: {
    title: 'Resource Efficiency',
    cpu: 'CPU',
    memory: 'RAM',
    savings: 'Potential savings',
    totalSavings: 'Total potential savings',
    noData: 'No efficiency data available.',
    peak7d: 'peak 7d',
    cpuOk: 'Good utilization — peak 7d: {used} of {requested}.',
    cpuIdle: 'No CPU usage (7d). Request is {requested} — consider scaling down.',
    cpuReduce: 'Peak 7d: {used} of {requested}. Consider reducing to ~{suggested}.',
    cpuIncrease: 'Peak 7d: {used}, only {requested} requested (limit: {limit}). Increase to ~{suggested}.',
    cpuIncreaseNoLimit: 'Peak 7d: {used}, only {requested} requested. Increase to ~{suggested}.',
    memOk: 'Good utilization — peak 7d: {used} of {requested}.',
    memIdle: 'No memory usage (7d). Request is {requested} — consider scaling down.',
    memReduce: 'Peak 7d: {used} of {requested}. Consider reducing to ~{suggested}.',
    memIncrease: 'Peak 7d: {used}, only {requested} requested (limit: {limit}). Increase to ~{suggested}.',
    memIncreaseNoLimit: 'Peak 7d: {used}, only {requested} requested. Increase to ~{suggested}.'
  },
  planManagement: {
    title: 'Plan Management',
    currentPlan: 'Current: {name}, {price}',
    downgrade: 'Downgrade',
    upgrade: 'Upgrade',
    current: 'Current',
    payg: {
      badge: 'save {percent}%',
      description: 'Based on your usage: ~{price}/mo',
      cta: 'Switch to Pay-as-you-go'
    },
    dialog: {
      title: 'Switch Plan',
      tierTitle: 'Switch to {name}',
      paygTitle: 'Switch to Pay-as-you-go',
      from: 'Current',
      to: 'New Plan',
      price: 'Price',
      quotas: 'Resource Quotas',
      cpu: 'CPU',
      memory: 'Memory',
      proration: 'Your current plan will be prorated. The new plan starts immediately.',
      paygExplain: 'Pay-as-you-go bills based on actual resource usage. No commitment, cancel anytime.',
      quotaUpgrade: 'Quotas increase to {cpu} vCPU / {memory} GiB RAM.',
      confirm: 'Confirm Switch',
      cancel: 'Cancel',
      success: 'Plan switched successfully',
      error: 'Failed to switch plan'
    }
  }
} as const
